import { useEffect, useState } from 'react';
import { loginCopy } from '../components/login/copy';
import { homeFor } from '../lib/rbac';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { storeApi, saveStoreSession, storeError } from '../lib/storeApi';
import { readSignOut, clearSignOut, storageBlocked, readAuthFailure, lsGet, lsSet } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ShieldCheck, MailCheck, ArrowLeft, Building2, Store, KeyRound, UserRound } from 'lucide-react';
import { toast } from 'sonner';

// A sign-out should never be a silent trip back to this form — say what happened.
const SIGN_OUT_NOTICE = {
  device: 'This session was signed out. Please sign in again — your other devices stay signed in.',
  ended: 'This session has ended. Please sign in again.',
  expired: 'Your sign-in is no longer valid. Please sign in again.',
  offline: 'The connection to the server dropped, so we could not confirm your sign-in. Please sign in again.',
  blocked: 'Something between this browser and the server blocked the app — usually network security, '
         + 'a firewall or a browser extension. Sign in again, and if it keeps happening send us the line below.',
  storage: 'This browser is not letting the app remember your sign-in, so it was lost on reload. '
         + 'Please sign in again — and if it keeps happening, allow cookies and site data for this site.',
  self: '',
};

const DEVICE_KEY = 'sahakar_store_device_id';

const storeDeviceId = () => {
  try {
    let id = lsGet(DEVICE_KEY);
    if (!id) { id = `st-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; lsSet(DEVICE_KEY, id); }
    return id;
  } catch { return ''; }
};

export default function Login() {
  const { user, login, verifyOtp, resendOtp } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(['store', 'staff'].includes(params.get('mode'))
    ? params.get('mode') : 'office');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otp, setOtp] = useState(null); // { challenge, emailHint, methods }
  const [method, setMethod] = useState('email'); // email | totp | recovery
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [blocked] = useState(() => storageBlocked());
  const [techDetail] = useState(() => readAuthFailure());
  const [notice, setNotice] = useState(() => {
    const { reason, ts } = readSignOut();
    if (!reason || Date.now() - ts > 120000) return '';
    // The blocked-storage banner below already explains that case.
    if (reason === 'storage') return '';
    return SIGN_OUT_NOTICE[reason] || '';
  });

  useEffect(() => {
    if (!cooldown) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (user && mode !== 'store') return <Navigate to={homeFor(user.role)} replace />;

  const isStore = mode === 'store';
  const isStaff = mode === 'staff';

  const switchMode = (m) => {
    setMode(m); setOtp(null); setError(''); setCode(''); setPassword('');
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError(''); setNotice(''); setLoading(true);
    clearSignOut();

    if (isStore) {
      try {
        const { data } = await storeApi.post('/store/auth/login', {
          email: email.trim(), password, device_id: storeDeviceId(),
        });
        setOtp({ challenge: data.challenge, emailHint: data.email_hint });
        setCode(''); setCooldown(data.retry_after || 60);
        toast.info(`We emailed a 6-digit code to ${data.email_hint}`);
      } catch (err) {
        setError(storeError(err, 'Wrong store email or password'));
      } finally { setLoading(false); }
      return;
    }

    const r = await login(email, password);
    setLoading(false);
    if (r.ok) { toast.success('Welcome to Sahakar Group of Companies'); nav(homeFor(r.user?.role)); return; }
    if (r.otpRequired) {
      const methods = r.methods || ['email'];
      setOtp({ challenge: r.challenge, emailHint: r.emailHint, methods });
      setMethod(methods.includes('totp') ? 'totp' : 'email');
      setCode(''); setCooldown(r.emailSent ? 60 : 0);
      if (r.emailSent) toast.info(`We emailed a 6-digit code to ${r.emailHint}`);
      else toast.info('Enter the code from your authenticator app');
      return;
    }
    setError(r.error);
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    setError(''); setLoading(true);
    if (isStore) {
      try {
        const { data } = await storeApi.post('/store/auth/verify-otp', { challenge: otp.challenge, code });
        saveStoreSession(data.token, data.store);
        toast.success(`${data.store.entity_name} · counter open`);
        nav('/store', { replace: true });
      } catch (err) {
        setError(storeError(err, 'Incorrect code'));
      } finally { setLoading(false); }
      return;
    }
    const r = await verifyOtp(otp.challenge, code, method);
    setLoading(false);
    if (r.ok) { toast.success('Device verified — welcome back'); nav(homeFor(r.user?.role)); }
    else setError(r.error);
  };

  const doResend = async () => {
    if (isStore) {
      try {
        const { data } = await storeApi.post('/store/auth/resend-otp', { challenge: otp.challenge });
        toast.success(data.resent ? 'New code sent' : 'A code was already sent — check the inbox');
        setCooldown(data.retry_after || 60);
      } catch (err) { setError(storeError(err, 'Could not resend the code')); }
      return;
    }
    const r = await resendOtp(otp.challenge);
    if (r.ok) {
      setMethod('email');
      toast.success(r.resent ? `Code emailed to ${r.email_hint}` : 'A code was already sent — check your inbox');
      setCooldown(r.retry_after || 60);
    } else setError(r.error);
  };

  const copy = loginCopy({ isStore, isStaff, otp, method });
  const { heading, sub } = copy;

  return (
    <div className="min-h-screen flex bg-background pwa-safe-top pwa-safe-bottom">
      {/* Left panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-zinc-950 grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(16,185,129,0.18),transparent_40%),radial-gradient(circle_at_80%_70%,rgba(59,130,246,0.12),transparent_40%)]"/>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src="/shp-icon.png" alt="Sahakar" className="w-12 h-12 rounded-2xl shadow-2xl shadow-black/40"/>
            <div>
              <div className="font-heading font-bold text-lg leading-tight">Sahakar Group of Companies</div>
              <div className="text-xs uppercase tracking-widest text-zinc-400">FMS Solution</div>
            </div>
          </div>
          <div className="space-y-5 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/10 text-xs uppercase tracking-wider">
              <ShieldCheck size={14}/> {copy.badge}
            </div>
            <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
              {copy.before}<span className={copy.color}>{copy.emphasis}</span>{copy.after}
            </h1>
            <p className="text-zinc-300 text-base leading-relaxed">
              {copy.description}
            </p>
            <div className="grid grid-cols-2 gap-3 pt-4">
              {(isStore
                ? ['Billing & payment modes', 'Cash box & deposits', 'Credit register', 'Shift & day close']
                : isStaff
                ? ['My duty roster', 'My billing PIN', 'My salary & advances', 'My home deliveries']
                : ['Sales & cash, every outlet', 'Approvals & payouts', 'Daily collections & transfers', 'Every entry, audited']
              ).map(t => (
                <div key={t} className="flex items-center gap-2 text-sm text-zinc-200">
                  <span className={`w-1.5 h-1.5 rounded-full ${isStore ? 'bg-lime-400' : isStaff ? 'bg-sky-400' : 'bg-emerald-400'}`}/>{t}
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-zinc-500">© Sahakar Group · Confidential</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/shp-icon.png" alt="Sahakar" className="w-10 h-10 rounded-xl shadow-lg"/>
            <div className="leading-tight">
              <div className="font-heading font-bold text-base">Sahakar</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Finance Command</div>
            </div>
          </div>

          {!otp && (
            <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-muted mb-7" data-testid="login-mode-switch">
              {[
                { key: 'office', label: 'Head office', icon: Building2 },
                { key: 'store', label: 'Store counter', icon: Store },
                { key: 'staff', label: 'Store staff', icon: UserRound },
              ].map(m => (
                <button key={m.key} type="button" onClick={() => switchMode(m.key)}
                  data-testid={`login-mode-${m.key}`}
                  className={`flex items-center justify-center gap-1.5 h-9 rounded-lg text-sm font-semibold transition-colors ${
                    mode === m.key ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                  <m.icon size={14}/>{m.label}
                </button>
              ))}
            </div>
          )}

          <h2 className="font-heading text-3xl font-bold tracking-tight">{heading}</h2>
          <p className="text-sm text-muted-foreground mt-1.5 mb-7">{sub}</p>

          {otp ? (
            <form onSubmit={submitOtp} className="space-y-4" data-testid="otp-form">
              <div className="flex items-center gap-2 text-xs text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2">
                <MailCheck size={14}/> {isStore
                  ? 'Every counter sign-in needs an emailed code.'
                  : 'New device detected — this keeps your account safe.'}
              </div>
              {!isStore && (otp.methods || []).includes('totp') && (
                <div className="flex flex-wrap gap-2" data-testid="otp-method-switch">
                  <button type="button" onClick={() => { setMethod('totp'); setCode(''); setError(''); }}
                    data-testid="otp-method-totp"
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${method === 'totp'
                      ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground'}`}>
                    <KeyRound size={12} className="inline mr-1"/>Authenticator app
                  </button>
                  {(otp.methods || []).includes('email') && (
                    <button type="button" onClick={() => { setCode(''); setError(''); doResend(); }}
                      data-testid="otp-method-email"
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${method === 'email'
                        ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground'}`}>
                      <MailCheck size={12} className="inline mr-1"/>Email me a code
                    </button>
                  )}
                  <button type="button" onClick={() => { setMethod('recovery'); setCode(''); setError(''); }}
                    data-testid="otp-method-recovery"
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${method === 'recovery'
                      ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground'}`}>
                    Use a recovery code
                  </button>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="otp" className="text-xs font-semibold uppercase tracking-wider">
                  {method === 'recovery' ? 'Recovery code' : method === 'totp' ? 'Code from your authenticator app' : '6-digit code'}
                </Label>
                {method === 'recovery' ? (
                  <Input id="otp" data-testid="otp-code" value={code} autoFocus autoComplete="off"
                         maxLength={11} placeholder="ABCD-1234"
                         onChange={e => setCode(e.target.value.toUpperCase().slice(0, 11))}
                         className="h-12 text-center text-xl tracking-[0.2em] font-semibold"/>
                ) : (
                  <Input id="otp" data-testid="otp-code" value={code} inputMode="numeric" autoComplete="one-time-code"
                         maxLength={6} autoFocus
                         onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                         className="h-12 text-center text-2xl tracking-[0.5em] font-semibold"/>
                )}
              </div>
              {error && <div className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2" data-testid="otp-error">{error}</div>}
              <Button type="submit" className="w-full h-11 font-semibold" data-testid="otp-submit"
                disabled={loading || (method === 'recovery' ? code.length < 6 : code.length !== 6)}>
                {loading ? <Loader2 className="animate-spin" size={16}/> : isStore ? 'Verify & open counter' : 'Verify & sign in'}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => { setOtp(null); setError(''); }} className="text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="otp-back">
                  <ArrowLeft size={12}/> Back to sign in
                </button>
                {(isStore || method === 'email') && (
                  <button type="button" onClick={doResend} disabled={cooldown > 0} className="text-accent disabled:text-muted-foreground" data-testid="otp-resend">
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {isStore
                  ? 'The counter stays open for 16 hours. Several counters of the same outlet can be signed in at the same time.'
                  : method === 'recovery'
                    ? 'Each recovery code works once. Generate a new set from Security afterwards.'
                    : 'Once verified, this device stays trusted for 5 days and you stay signed in until you sign out.'}
              </p>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">
                {isStore ? 'Store email' : 'Email'}
              </Label>
              <Input id="email" data-testid="login-email" type="email" value={email} autoComplete="username"
                     onChange={e => setEmail(e.target.value)} required className="h-11"
                     placeholder={isStore ? 'outlet@sahakar.com' : ''}/>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">
                {isStore ? 'Store password' : 'Password'}
              </Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                     autoComplete="current-password"
                     onChange={e => setPassword(e.target.value)} required className="h-11"/>
            </div>
            {notice && <div className="text-sm text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2" data-testid="login-device-notice">{notice}</div>}
            {notice && techDetail && (
              <div className="text-[11px] text-muted-foreground font-mono break-all" data-testid="login-tech-detail">
                {techDetail}
              </div>
            )}
            {blocked && (
              <div
                className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2"
                data-testid="login-storage-warning"
              >
                This browser is blocking site storage, so a sign-in here will be lost as soon as the page
                reloads. Allow cookies and site data for this site, or turn off private/incognito browsing.
              </div>
            )}
            {error && <div className="text-sm text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 font-semibold" data-testid="login-submit">
              {loading ? <Loader2 className="animate-spin" size={16}/> : isStore ? 'Continue' : 'Sign in'}
            </Button>
            {isStore && (
              <p className="text-[11px] text-muted-foreground">
                A 6-digit code is emailed to the outlet address every time. Staff then confirm each entry with
                their own private PIN.
              </p>
            )}
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
